// Package Cost Components loader (v3.5.41)
//
// Mirrors loadPackageIncludes (v3.5.31) but for the package_cost_components
// table introduced in v3.5.40. Returns an array shaped for direct use by
// calcQuote() and calcLocationMRR() in pricing.js.
//
// Each returned item has the cost component config (qty_driver, qty_per_unit,
// min_qty) plus a snapshot of the linked product's name and cost_price. The
// engine sums these into toolingCost when a package has use_cost_components=true
// AND has at least one active row.

import { supabase } from './supabase';

/**
 * Load active cost components for a package, joined with product details.
 *
 * @param {string} packageId — the package's UUID
 * @returns {Promise<Array<{
 *   id: string, product_id: string, qty_driver: string,
 *   qty_per_unit: number, min_qty: number, notes: string|null,
 *   sort_order: number,
 *   product_name: string, cost_price: number, internal_only: boolean,
 *   category: string|null
 * }>>}
 *
 * Returns [] when no components configured or on any error. Errors are logged.
 * Inactive products are still returned (caller can flag them in UI) — same
 * pattern as loadPackageIncludes.
 */
export async function loadPackageCostComponents(packageId) {
  if (!packageId) return [];

  const { data: compRows, error: compErr } = await supabase
    .from('package_cost_components')
    .select('*')
    .eq('package_id', packageId)
    .eq('active', true)
    .order('sort_order');
  if (compErr) {
    // If the table doesn't exist yet (migration not run), this surfaces here.
    // We log and return [] so the engine falls through to aggregate settings.
    console.error('loadPackageCostComponents: fetch failed', compErr);
    return [];
  }
  if (!compRows || compRows.length === 0) return [];

  const productIds = [...new Set(compRows.map(r => r.product_id))];
  const { data: prodRows, error: prodErr } = await supabase
    .from('products')
    .select('id, name, category, cost_price, internal_only, active')
    .in('id', productIds);
  if (prodErr) {
    console.error('loadPackageCostComponents: product fetch failed', prodErr);
    return [];
  }
  const prodById = Object.fromEntries((prodRows || []).map(p => [p.id, p]));

  return compRows
    .map(c => {
      const p = prodById[c.product_id];
      if (!p) {
        console.warn('loadPackageCostComponents: product missing for component', c.id, c.product_id);
        return null;
      }
      return {
        id:             c.id,
        product_id:     c.product_id,
        qty_driver:     c.qty_driver,
        qty_per_unit:   parseFloat(c.qty_per_unit) || 1.0,
        min_qty:        parseInt(c.min_qty) || 0,
        notes:          c.notes || null,
        sort_order:     c.sort_order ?? 100,
        // Product snapshot
        product_name:   p.name,
        category:       p.category || null,
        cost_price:     Number(p.cost_price) || 0,
        internal_only:  !!p.internal_only,
        active:         p.active !== false,
      };
    })
    .filter(Boolean);
}
